import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import UploadDocumentModal from '@/pages/documents/components/UploadDocumentModal';
import DocumentDetailModal from '@/pages/documents/components/DocumentDetailModal';
import EditCustomerModal from '@/pages/customers/components/EditCustomerModal';
import DeleteConfirmModal from '@/components/base/DeleteConfirmModal';
import CreateKycModal from '@/pages/kyc-requests/components/CreateKycModal';
import DocumentThumbnail from '@/components/feature/DocumentThumbnail';

interface CustomerDoc {
  id: number;
  docType: string;
  docCategory: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  verificationStatus: string;
  reviewReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  uploadedByName: string | null;
  ocr_data?: {
    rawText: string;
    fields: Record<string, string | null>;
    confidence: number;
    processedAt: string;
    engine: string;
  } | null;
  verification_result?: {
    compositeScore: number;
    riskLevel: string;
    flags: { type: string; label: string; severity: string }[];
    processedAt: string;
  } | null;
}

interface LinkedCompany {
  id: number;
  companyName: string;
}

interface KycRequest {
  id: number;
  requestNumber: string;
  status: string;
  riskLevel: string;
  createdAt: string;
  reviewedAt: string | null;
}

interface CustomerDetail {
  id: number;
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  idType: string | null;
  idNumber: string | null;
  nationality: string | null;
  occupation: string | null;
  customerType: string;
  status: string;
  riskLevel: string;
  notes: string | null;
  isDeleted: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  profileCompleteness?: number;
  companyRequiredDocuments?: string[] | null;
  linkedCompanies?: LinkedCompany[];
  documents?: CustomerDoc[];
  kycRequests?: KycRequest[];
}

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  sole_proprietor: 'Sole Proprietor',
  private_limited: 'Private Limited',
  public_limited: 'Public Limited',
  llp: 'LLP',
  partnership: 'Partnership',
  trust: 'Trust',
  ngo: 'NGO',
  others: 'Others',
};

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

function calculateAge(dob: string | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocCategory(category: string): string {
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { put, del } = useApi();
  const customerId = Number(id);

  const { data: customer, loading, error, refetch } = useApiQuery<CustomerDetail>({
    endpoint: `/customers/${customerId}`,
    enabled: !!id && !isNaN(customerId),
  });

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [detailDoc, setDetailDoc] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; mode: 'soft' | 'hard' } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createKycOpen, setCreateKycOpen] = useState(false);

  // Company transfer (superadmin)
  const [transferOpen, setTransferOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  const { data: companiesData } = useApiQuery<{ companies?: { id: number; name: string }[] }>({
    endpoint: '/companies',
    enabled: isSuperAdmin && !!customer,
  });
  const companiesList = companiesData?.companies || [];

  const canManage = user?.role === 'superadmin' || user?.role === 'company_admin' || user?.role === 'kyc_officer';
  const isSuperAdmin = user?.role === 'superadmin';
  const isIndividual = customer?.customerType === 'individual';

  const currentCompanyName = companiesList.find((c) => c.id === customer?.companyId)?.name || `Company #${customer?.companyId ?? 'Unknown'}`;

  const requiredDocs = customer?.companyRequiredDocuments || [];
  const uploadedRequiredCount = requiredDocs.filter((req) =>
    customer?.documents?.some((d) => d.docCategory === req)
  ).length;
  const missingCount = requiredDocs.length - uploadedRequiredCount;

  const [activateLoading, setActivateLoading] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  const handleActivate = useCallback(async () => {
    if (!customer) return;
    setActivateLoading(true);
    setActivateError(null);
    try {
      await put(`/customers/${customer.id}/activate`, {});
      refetch();
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : 'Failed to activate');
    } finally {
      setActivateLoading(false);
    }
  }, [customer, put, refetch]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'soft' || !customer) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await put(`/customers/${deleteTarget.id}/soft-delete`, {});
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate');
    } finally {
      setActionLoading(false);
    }
  }, [deleteTarget, customer, put, refetch]);

  const handleHardDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'hard') return;
    setActionLoading(true);
    setActionError(null);
    try {
      await del(`/customers/${deleteTarget.id}`);
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  }, [deleteTarget, del, refetch]);

  const handleRestore = useCallback(async () => {
    if (!customer || !customer.isDeleted) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await put(`/customers/${customer.id}/restore`, {});
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to restore');
    } finally {
      setActionLoading(false);
    }
  }, [customer, put, refetch]);

  const handleTransferCompany = useCallback(async () => {
    if (!customer || !selectedCompanyId) return;
    setTransferLoading(true);
    setTransferError(null);
    try {
      await put(`/customers/${customer.id}`, { companyId: Number(selectedCompanyId) });
      setTransferOpen(false);
      setSelectedCompanyId('');
      refetch();
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : 'Failed to transfer customer');
    } finally {
      setTransferLoading(false);
    }
  }, [customer, selectedCompanyId, put, refetch]);

  const typeColors: Record<string, string> = {
    individual: 'bg-sky-50 text-sky-700',
    sole_proprietor: 'bg-violet-50 text-violet-700',
    private_limited: 'bg-teal-50 text-teal-700',
    public_limited: 'bg-orange-50 text-orange-700',
    llp: 'bg-rose-50 text-rose-700',
    partnership: 'bg-indigo-50 text-indigo-700',
    trust: 'bg-amber-50 text-amber-700',
    ngo: 'bg-emerald-50 text-emerald-700',
    others: 'bg-stone-100 text-stone-600',
  };

  const riskColors: Record<string, string> = {
    low: 'bg-emerald-50 text-emerald-700',
    medium: 'bg-amber-50 text-amber-700',
    high: 'bg-red-50 text-red-700',
  };

  const statusColors: Record<string, string> = {
    active: 'bg-emerald-50 text-emerald-700',
    inactive: 'bg-stone-100 text-stone-600',
    under_review: 'bg-amber-50 text-amber-700',
    pending: 'bg-sky-50 text-sky-700',
  };

  const docStatusColors: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700',
    verified: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center" />
          <span className="text-stone-500 text-sm">Loading customer details...</span>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-4 md:p-8 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-user-line text-stone-300 text-3xl mb-2 w-12 h-12 flex items-center justify-center mx-auto" />
          <p className="text-stone-500 text-sm">{error || 'Customer not found.'}</p>
          <Link to="/customers" className="text-brand-600 text-sm mt-3 inline-block hover:underline">
            Back to Customers
          </Link>
        </div>
      </div>
    );
  }

  const fullName = `${customer.firstName} ${customer.lastName}`;
  const age = calculateAge(customer.dateOfBirth);

  return (
    <div className="p-4 md:p-8 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link
            to="/customers"
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 transition-colors"
          >
            <i className="ri-arrow-left-line text-lg w-5 h-5 flex items-center justify-center" />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-stone-900">{fullName}</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[customer.customerType] || 'bg-stone-100 text-stone-600'}`}>
                {CUSTOMER_TYPE_LABELS[customer.customerType] || customer.customerType}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[customer.status] || 'bg-stone-100 text-stone-600'}`}>
                {customer.isDeleted ? 'Deleted' : customer.status}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskColors[customer.riskLevel] || 'bg-stone-100 text-stone-700'}`}>
                {customer.riskLevel} risk
              </span>
            </div>
            <p className="text-stone-500 text-sm mt-0.5">{customer.email} {customer.phone ? `· ${customer.phone}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && !customer.isDeleted && (
            <>
              <button
                onClick={() => setEditModalOpen(true)}
                className="bg-white border border-stone-200 hover:bg-stone-50 text-stone-700 font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-edit-line" />
                Edit
              </button>
              {isSuperAdmin && customer.status !== 'active' && (customer.profileCompleteness ?? 0) < 60 && (
                <button
                  onClick={handleActivate}
                  disabled={activateLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                >
                  {activateLoading ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-check-line" />
                  )}
                  Activate
                </button>
              )}
              <button
                onClick={() => setCreateKycOpen(true)}
                className="bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shield-user-line" />
                Initiate eKYC
              </button>
              <button
                onClick={() => setUploadModalOpen(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-upload-cloud-line" />
                Upload Doc
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => setTransferOpen(true)}
                  className="bg-white border border-stone-200 hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200 text-stone-600 font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-exchange-line" />
                  Transfer Company
                </button>
              )}
              <button
                onClick={() => setDeleteTarget({ id: customer.id, name: fullName, mode: 'soft' })}
                className="bg-white border border-stone-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-stone-600 font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-line" />
                Deactivate
              </button>
            </>
          )}
          {customer.isDeleted && canManage && (
            <>
              <button
                onClick={handleRestore}
                disabled={actionLoading}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-go-back-line" />
                Reactivate
              </button>
              <button
                onClick={() => setDeleteTarget({ id: customer.id, name: fullName, mode: 'hard' })}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line" />
                Delete Permanently
              </button>
            </>
          )}
        </div>
      </div>

      {actionError && (
        <div className="mb-4 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
          {actionError}
        </div>
      )}

      {/* Ready for Review Banner */}
      {(customer.profileCompleteness ?? 0) >= 60 && customer.status !== 'active' && !customer.isDeleted && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-shield-check-line text-emerald-600 text-lg w-5 h-5 flex items-center justify-center" />
            </div>
            <div>
              <p className="text-emerald-900 text-sm font-bold">Ready for Review</p>
              <p className="text-emerald-700 text-xs mt-0.5">
                Profile completeness is at {customer.profileCompleteness}%. All required documents appear to be in place — review and activate this customer.
              </p>
            </div>
          </div>
          {isSuperAdmin && (
            <button
              onClick={handleActivate}
              disabled={activateLoading}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold px-5 py-2.5 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer shrink-0"
            >
              {activateLoading ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-check-line" />
              )}
              Activate Profile
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Full Name</p>
                <p className="text-stone-900 text-sm font-medium">{fullName}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Email</p>
                <p className="text-stone-900 text-sm">{customer.email}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Phone</p>
                <p className="text-stone-900 text-sm">{customer.phone || '-'}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Customer Type</p>
                <p className="text-stone-900 text-sm font-medium">
                  {CUSTOMER_TYPE_LABELS[customer.customerType] || customer.customerType}
                </p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Date of Birth</p>
                <p className="text-stone-900 text-sm">
                  {customer.dateOfBirth
                    ? `${new Date(customer.dateOfBirth).toLocaleDateString()} (${age} years)`
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Gender</p>
                <p className="text-stone-900 text-sm capitalize">{customer.gender || '-'}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Nationality</p>
                <p className="text-stone-900 text-sm">{customer.nationality || '-'}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Occupation</p>
                <p className="text-stone-900 text-sm">{customer.occupation || '-'}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Address</p>
                <p className="text-stone-900 text-sm">
                  {customer.address
                    ? `${customer.address}${customer.city ? `, ${customer.city}` : ''}${customer.state ? `, ${customer.state}` : ''}${customer.country ? `, ${customer.country}` : ''} ${customer.postalCode || ''}`
                    : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Required Documents Checklist */}
          {requiredDocs.length > 0 && (
            <div className="bg-white rounded-xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-stone-900">Required Documents</h3>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${missingCount === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {uploadedRequiredCount} of {requiredDocs.length} uploaded
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {requiredDocs.map((doc) => {
                  const isUploaded = customer.documents?.some((d) => d.docCategory === doc);
                  return (
                    <div
                      key={doc}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border ${isUploaded ? 'border-emerald-100 bg-emerald-50/40' : 'border-stone-100 bg-stone-50/60'}`}
                    >
                      <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0">
                        {isUploaded ? (
                          <i className="ri-check-line text-emerald-600 text-sm w-4 h-4 flex items-center justify-center" />
                        ) : (
                          <i className="ri-close-line text-stone-300 text-sm w-4 h-4 flex items-center justify-center" />
                        )}
                      </div>
                      <span className={`text-sm ${isUploaded ? 'text-emerald-800 font-medium' : 'text-stone-500'}`}>
                        {formatDocCategory(doc)}
                      </span>
                    </div>
                  );
                })}
              </div>
              {missingCount > 0 && canManage && (
                <button
                  onClick={() => setUploadModalOpen(true)}
                  className="mt-3 text-brand-600 text-xs font-medium hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-upload-cloud-line w-3.5 h-3.5 flex items-center justify-center" />
                  Upload missing documents
                </button>
              )}
            </div>
          )}

          {/* Identity Document */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">Identity Document</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">ID Type</p>
                <p className="text-stone-900 text-sm font-medium">{customer.idType || '-'}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">ID Number</p>
                <p className="text-stone-900 text-sm">{customer.idNumber || '-'}</p>
              </div>
            </div>
          </div>

          {/* Linked Companies (non-individual) */}
          {!isIndividual && customer.linkedCompanies && customer.linkedCompanies.length > 0 && (
            <div className="bg-white rounded-xl border border-stone-100 p-6">
              <h3 className="font-bold text-stone-900 mb-4">Linked Companies</h3>
              <div className="flex flex-col gap-2">
                {customer.linkedCompanies.map((company, idx) => (
                  <Link
                    key={company.id}
                    to={`/companies/${company.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-stone-100 hover:border-brand-200 hover:bg-brand-50/20 transition-colors"
                  >
                    <span className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-stone-900 text-sm font-medium">{company.companyName}</p>
                    <i className="ri-arrow-right-s-line text-stone-400 text-sm ml-auto w-4 h-4 flex items-center justify-center" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-stone-900">Documents</h3>
              <span className="text-stone-400 text-xs">{customer.documents?.length || 0} total</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {customer.documents && customer.documents.length > 0 ? (
                customer.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-stone-100 hover:border-brand-200 transition-colors cursor-pointer"
                    onClick={() => setDetailDoc(doc.id)}
                  >
                    <DocumentThumbnail
                      documentId={doc.id}
                      mimeType={doc.mimeType}
                      fileName={doc.fileName}
                      size="md"
                      onClick={() => setDetailDoc(doc.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-stone-900 text-sm font-medium truncate">{doc.fileName}</p>
                      <p className="text-stone-400 text-xs">{doc.docType} · {formatSize(doc.fileSize)}</p>
                      {doc.uploadedByName && (
                        <p className="text-stone-400 text-[10px]">By {doc.uploadedByName}</p>
                      )}
                      {doc.verificationStatus === 'failed' && doc.reviewReason && (
                        <div className="mt-1 bg-red-50 border border-red-100 rounded-md px-2 py-1">
                          <p className="text-red-700 text-[10px]"><strong>Rejected:</strong> {doc.reviewReason}</p>
                        </div>
                      )}
                      {doc.verificationStatus === 'verified' && (
                        <div className="mt-1 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1">
                          <p className="text-emerald-700 text-[10px]">
                            <strong>Approved</strong>
                            {doc.reviewedAt && ` · ${new Date(doc.reviewedAt).toLocaleDateString()}`}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${docStatusColors[doc.verificationStatus] || 'bg-stone-100 text-stone-700'}`}>
                        {doc.verificationStatus}
                      </span>
                      {doc.ocr_data && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-violet-50 text-violet-700 flex items-center gap-0.5">
                          <i className="ri-scan-line" />
                          OCR
                        </span>
                      )}
                      {doc.verification_result && doc.verification_result.riskLevel !== 'low' && (
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium flex items-center gap-0.5 ${
                          doc.verification_result.riskLevel === 'high'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>
                          <i className="ri-alarm-warning-line" />
                          {doc.verification_result.riskLevel === 'high' ? 'Suspicious' : 'Caution'}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-6">
                  <i className="ri-file-list-3-line text-stone-300 text-2xl mb-2 w-10 h-10 flex items-center justify-center mx-auto" />
                  <p className="text-stone-400 text-sm">No documents uploaded yet.</p>
                  {canManage && (
                    <button
                      onClick={() => setUploadModalOpen(true)}
                      className="text-brand-600 text-sm mt-2 hover:underline cursor-pointer"
                    >
                      Upload a document
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* KYC Requests */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">KYC Requests</h3>
            <div className="flex flex-col gap-2">
              {customer.kycRequests && customer.kycRequests.length > 0 ? (
                customer.kycRequests.map((kyc) => (
                  <Link
                    key={kyc.id}
                    to={`/kyc/${kyc.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-stone-100 hover:border-brand-200 hover:bg-brand-50/20 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                        <i className="ri-file-list-3-line text-stone-400 text-sm w-4 h-4 flex items-center justify-center" />
                      </div>
                      <div>
                        <p className="text-stone-900 text-sm font-medium">{kyc.requestNumber}</p>
                        <p className="text-stone-400 text-xs">
                          Created {new Date(kyc.createdAt).toLocaleDateString()}
                          {kyc.reviewedAt && ` · Reviewed ${new Date(kyc.reviewedAt).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskColors[kyc.riskLevel] || 'bg-stone-100 text-stone-700'}`}>
                        {kyc.riskLevel}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        kyc.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                        kyc.status === 'rejected' ? 'bg-red-50 text-red-700' :
                        kyc.status === 'under review' ? 'bg-amber-50 text-amber-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {kyc.status}
                      </span>
                      <i className="ri-arrow-right-s-line text-stone-400 text-sm w-4 h-4 flex items-center justify-center" />
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-6">
                  <i className="ri-file-list-3-line text-stone-300 text-2xl mb-2 w-10 h-10 flex items-center justify-center mx-auto" />
                  <p className="text-stone-400 text-sm">No KYC requests yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column - Meta */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-xl border border-stone-100 p-5">
            <h4 className="font-bold text-stone-900 text-sm mb-3">Status Overview</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-stone-500 text-xs">Status</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[customer.status] || 'bg-stone-100 text-stone-600'}`}>
                  {customer.isDeleted ? 'Deleted' : customer.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500 text-xs">Risk Level</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskColors[customer.riskLevel] || 'bg-stone-100 text-stone-700'}`}>
                  {customer.riskLevel}
                </span>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-stone-500 text-xs">Profile Completeness</span>
                  <span className="text-stone-900 text-xs font-medium">{customer.profileCompleteness ?? 0}%</span>
                </div>
                <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      (customer.profileCompleteness ?? 0) >= 80 ? 'bg-emerald-500' : (customer.profileCompleteness ?? 0) >= 50 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, customer.profileCompleteness ?? 0)}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500 text-xs">Documents</span>
                <span className="text-stone-900 text-xs font-medium">{customer.documents?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500 text-xs">KYC Requests</span>
                <span className="text-stone-900 text-xs font-medium">{customer.kycRequests?.length || 0}</span>
              </div>
              {isSuperAdmin && (
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Company</span>
                  <span className="text-stone-900 text-xs font-medium">{currentCompanyName}</span>
                </div>
              )}
              {customer.isDeleted && customer.deletedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Deleted On</span>
                  <span className="text-red-600 text-xs font-medium">{new Date(customer.deletedAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h4 className="font-bold text-stone-900 text-sm mb-2">Notes</h4>
              <p className="text-stone-600 text-sm leading-relaxed whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-white rounded-xl border border-stone-100 p-5">
            <h4 className="font-bold text-stone-900 text-sm mb-3">Timestamps</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-stone-500 text-xs">Created</span>
                <span className="text-stone-900 text-xs">{new Date(customer.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500 text-xs">Updated</span>
                <span className="text-stone-900 text-xs">{new Date(customer.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UploadDocumentModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={refetch}
        kycRequestId={undefined}
      />

      <DocumentDetailModal
        isOpen={!!detailDoc}
        onClose={() => setDetailDoc(null)}
        documentId={detailDoc ?? null}
      />

      <EditCustomerModal
        open={editModalOpen}
        customer={customer}
        onClose={() => setEditModalOpen(false)}
        onSuccess={refetch}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.mode === 'hard' ? handleHardDelete : handleSoftDelete}
        title={deleteTarget?.mode === 'hard' ? 'Permanent Delete' : 'Deactivate Customer'}
        entityName={deleteTarget?.name}
        description={
          deleteTarget?.mode === 'hard'
            ? 'This will permanently erase the customer profile, all KYC requests, and every uploaded document from disk. This action cannot be undone.'
            : 'The customer profile will be disabled and hidden from the list. You can reactivate it later.'
        }
      />

      <CreateKycModal
        open={createKycOpen}
        onClose={() => setCreateKycOpen(false)}
        onSuccess={refetch}
        preselectedCustomerId={customerId}
      />

      {/* Transfer Company Modal */}
      {transferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setTransferOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
              <div>
                <h2 className="text-lg font-bold text-stone-900">Transfer Customer</h2>
                <p className="text-stone-400 text-xs mt-0.5">Move this customer to a different company</p>
              </div>
              <button
                onClick={() => setTransferOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {transferError && (
                <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
                  {transferError}
                </div>
              )}
              <div className="mb-1">
                <p className="text-stone-400 text-xs uppercase tracking-wider">Current Company</p>
                <p className="text-stone-900 text-sm font-medium mt-0.5">{currentCompanyName}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Select New Company</label>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Choose a company...</option>
                  {companiesList.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-stone-400 text-xs leading-relaxed">
                <i className="ri-information-line text-brand-600 mr-1" />
                This will move the customer and all their documents/KYC history to the selected company. Audit logs will record this change.
              </p>
              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setTransferOpen(false)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleTransferCompany}
                  disabled={!selectedCompanyId || transferLoading || String(selectedCompanyId) === String(customer?.companyId)}
                  className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {transferLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Transferring...
                    </>
                  ) : (
                    <>
                      <i className="ri-exchange-line" />
                      Confirm Transfer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}